import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Button, Field, Input, useToast } from '../../components/ui';
import { api, clearStoredToken } from '../../lib/api';
import { FullPageSpinner, loginProblem, normaliseLogin, useOnline, useStaffMe, type LoginProblem } from '../../lib/auth';

const timeOnly = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });

/** Seconds left until `until` (ms), ticking once a second; 0 once it has passed. */
function useCountdown(until: number | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [until]);
  return until ? Math.max(0, Math.ceil((until - now) / 1000)) : 0;
}

export default function StaffLogin() {
  const location = useLocation();
  // "See it as each user" (the simulation panel) opens this page with ?login=<demo login> filled in.
  const [wanted] = useState(() => normaliseLogin(new URLSearchParams(location.search).get('login') ?? '').slice(0, 60));
  const [login, setLogin] = useState(wanted);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const online = useOnline();
  // If /auth/me cannot be reached (no network, server restarting) the form still shows: signing in tries again.
  const { data: me, isLoading, isError: meFailed } = useStaffMe();
  const m = useMutation({
    mutationFn: () => api<{ me: unknown; mustChangePassword?: boolean; loginNow?: string }>('/auth/login', { body: { login: normaliseLogin(login), password } }),
    onMutate: () => setRetryAt(null),
    onError: (err) => {
      const p = loginProblem(err);
      if (p?.kind === 'slow') setRetryAt(Date.now() + p.seconds * 1000);
    },
    onSuccess: (r) => {
      clearStoredToken();
      qc.setQueryData(['staff-me'], r.me);
      // The login was renamed; the old name still works, but say what it is called now.
      if (r.loginNow) toast.success(`Signed in. This login is now called “${r.loginNow}”; the old name keeps working too.`);
      // A new or reset password must be replaced first: My account is the only page until it is.
      const must = r.mustChangePassword || (r.me as { mustChangePassword?: boolean } | null)?.mustChangePassword;
      navigate(must ? '/admin/account' : ((location.state as { from?: string } | null)?.from ?? '/admin'), { replace: true });
    },
  });
  const waitLeft = useCountdown(retryAt);

  if (isLoading) return <FullPageSpinner />;
  if (me && wanted && me.user.login !== wanted) return <SignedInAsSomeoneElse name={me.user.name} wanted={wanted} />;
  if (me) return <Navigate to="/admin" replace />;

  const problem = loginProblem(m.error);
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState?.('CapsLock') ?? false);
  const waiting = problem?.kind === 'slow' && waitLeft > 0;

  return (
    <div className="flex min-h-screen items-center justify-center bg-shell px-4 py-6">
      <form
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onSubmit={(e) => {
          e.preventDefault();
          if (normaliseLogin(login) && password && !waiting) m.mutate();
        }}
      >
        <p className="text-2xl font-bold tracking-tight text-brand-700">Carnival City</p>
        <p className="mb-6 mt-1 text-sm text-slate-500">Staff &amp; shop sign in</p>
        {(!online || meFailed) && !m.error && (
          <p role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {online ? 'The server is not answering right now. You can still try to sign in.' : 'This device is offline. Connect to Wi-Fi or mobile data to sign in.'}
          </p>
        )}
        <div className="flex flex-col gap-4">
          <Field label="Login">
            {(id) => (
              <Input
                id={id}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                onBlur={() => setLogin((v) => normaliseLogin(v))}
                onKeyDown={onKey}
                onKeyUp={onKey}
                className="h-12"
                required
              />
            )}
          </Field>
          <Field
            label="Password"
            hint={
              capsLock ? (
                <span className="font-medium text-amber-700" data-testid="caps-lock">
                  Caps Lock is on
                </span>
              ) : undefined
            }
          >
            {(id) => (
              <div className="relative">
                <Input
                  id={id}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={onKey}
                  onKeyUp={onKey}
                  autoFocus={!!wanted}
                  className="h-12 pr-20"
                  required
                />
                {/*
                  Named "Show password" / "Hide password" through its content, not aria-label: getByLabel('Password')
                  lookups (several test helpers and the UAT smoke test match loosely) then still find only the input.
                */}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  aria-controls={id}
                  className="absolute inset-y-1 right-1 min-w-11 rounded-md px-3 text-sm font-medium text-brand-700 hover:bg-brand-50"
                >
                  {showPassword ? 'Hide' : 'Show'}
                  <span className="sr-only"> password</span>
                </button>
              </div>
            )}
          </Field>
          {problem && (
            <div role="alert" data-problem={problem.kind} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <ProblemText problem={problem} waitLeft={waitLeft} />
            </div>
          )}
          <Button type="submit" size="lg" loading={m.isPending} disabled={waiting} className="w-full">
            {waiting ? `Wait ${waitLeft} s` : 'Sign in'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ProblemText({ problem, waitLeft }: { problem: LoginProblem; waitLeft: number }) {
  switch (problem.kind) {
    case 'wrong':
      return (
        <>
          <p className="font-medium">Invalid login or password.</p>
          <p className="mt-1 text-red-800/80">Logins are given by your super admin or shop admin. Check the spelling and try again.</p>
        </>
      );
    case 'disabled':
      return (
        <>
          <p className="font-medium">This login has been deactivated (switched off).</p>
          <p className="mt-1">Ask your shop admin, or the super admin, to turn it back on.</p>
        </>
      );
    case 'deleted':
      return (
        <>
          <p className="font-medium">This login was removed by the venue office.</p>
          <p className="mt-1">It cannot be used any more. Ask your shop admin, or the super admin, for a new login.</p>
        </>
      );
    case 'shop_closed':
      return (
        <>
          <p className="font-medium">Your shop has been closed or removed.</p>
          <p className="mt-1">Logins of this shop cannot sign in. Ask the venue office.</p>
        </>
      );
    case 'locked':
      return (
        <>
          <p className="font-medium">This login is locked on this device for now because of too many wrong passwords.</p>
          <p className="mt-1">
            {problem.until ? `Try again after ${timeOnly.format(problem.until)}` : 'Try again in 15 minutes'}, sign in from another device, or ask your admin to reset the
            password.
          </p>
        </>
      );
    case 'slow':
      return waitLeft > 0 ? (
        <p>
          {problem.deviceWide ? 'Too many sign-in attempts from this device.' : 'Too many wrong passwords for this login.'} Please slow down: wait{' '}
          <span className="font-semibold tabular" data-testid="slow-down-seconds">
            {waitLeft}
          </span>{' '}
          second{waitLeft === 1 ? '' : 's'} and try again.
        </p>
      ) : (
        <p>You can try again now. Check the password carefully first.</p>
      );
    case 'offline':
      return <p>Cannot reach the server. Check the Wi-Fi or mobile data and try again.</p>;
    case 'server':
      return <p>The server had a problem signing you in. Try again in a moment.</p>;
    default:
      return <p>{problem.message}</p>;
  }
}

/**
 * A link that asks for another login (e.g. "See it as each user") opened in a browser that is already signed
 * in. The browser holds one sign-in at a time, so say so instead of silently opening the current login's screens.
 */
function SignedInAsSomeoneElse({ name, wanted }: { name: string; wanted: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex min-h-screen items-center justify-center bg-shell px-4 py-6">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <p className="text-2xl font-bold tracking-tight text-brand-700">Carnival City</p>
        <h1 className="mt-4 text-base font-semibold text-slate-900">You are signed in as {name}</h1>
        <p className="mt-2 text-sm text-slate-600">
          To see the app as <span className="font-mono font-semibold">{wanted}</span>, sign out here first. This browser keeps one sign-in at a time, so your other tabs are
          signed out too. To stay signed in, open this link in a private (incognito) window instead.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <Button
            size="lg"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              await api('/auth/logout', { method: 'POST', body: {} }).catch(() => {});
              clearStoredToken();
              qc.setQueryData(['staff-me'], null);
              setBusy(false);
            }}
          >
            Sign out and continue as {wanted}
          </Button>
          <Button variant="secondary" size="lg" onClick={() => navigate('/admin', { replace: true })}>
            Stay signed in as {name}
          </Button>
        </div>
      </div>
    </div>
  );
}
